/* eslint-disable no-invalid-this */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import type {RootState} from 'appredux/reducers';
import {
  IFarmer,
  IFarmInputProduct,
  IFarmInputProgramInput,
  IProduce,
  ISoilData,
  ISoilSampling,
  ISupplierUserProfile,
  IFdApplicationType,
} from 'constants/interfaces';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  Radio,
  Typography,
  Snackbar,
  CircularProgress,
} from '@material-ui/core';
import {
  apiGenerateAiProgram,
  apiGenerateKhulaProgram,
  apiGetFarmer,
  apiGetProduce,
  apiLinkProgramToFundingApplication,
  apiGetUserFdApplications,
  apiGetProgramProducts,
} from 'constants/Api';
import {
  reduxAddCreateProgramLine,
  reduxMoveCreateProgramLineDown,
  reduxMoveCreateProgramLineUp,
  reduxRemoveCreateProgramLine,
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {NavigationService} from 'services/index';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPenToSquare,
  faSave,
  faArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import {faSparkles} from '@fortawesome/pro-solid-svg-icons';
import {saveAs} from 'file-saver';
import {
  buildProgramTemplate,
  parseProgramTemplate,
} from './utils/programImportExport';

import {ESidePanelView} from 'constants/index';
import {ProgramLineTable} from 'screens/Dashboard/Programs/ProgramCreate/ProgramLineTable';
import {ProductState} from 'appredux/reducers/reducer-products';
import {CreateProgramState} from 'appredux/reducers/reducer-create-program';
import Lottie from 'react-lottie';
import animationData from 'assets/animations/loading-ai.json';

import './ProgramCreate.scss';
import {ID} from 'aws-sdk/clients/s3';
import {useLocation} from 'react-router';

/* -------------------------------------------------------------------------- */
/*  helpers                                                                   */
/* -------------------------------------------------------------------------- */
const parseApplicationRate = (txt: string) => {
  const parts = txt.split(' ');
  if (parts.length !== 2) return {value: 0, unit: 'unit', coverage: 'unit'};
  const [v, per] = parts;
  const [unit, coverage] = per.split('/');
  return {value: parseFloat(v), unit, coverage};
};

/* ========================================================================== */
/*  MAIN COMPONENT                                                            */
/* ========================================================================== */
export function ProgramCreate(props: {
  user: ISupplierUserProfile;
  productState: ProductState;
  createProgramState: CreateProgramState;
}) {
  const location: any = useLocation();
  const farmerUserId = location?.state?.userId;
  const program = props.createProgramState.program;
  const programLines = props.createProgramState.lines;

  // Listen for side panel edit state changes
  const editState = useSelector(
    (state: RootState) => state.sidepanel?.editState,
  );

  /* ---------------- state ---------------- */
  const [products, setProducts] = useState<IFarmInputProduct[]>([]);
  const [allProduce, setAllProduce] = useState<IProduce[]>([]);
  const [farmer, setFarmer] = useState<IFarmer>();
  const [flags, setFlags] = useState({
    products: false,
    produce: false,
    farmer: false,
    creating: false,
    ai: false,
  });
  const [snack, setSnack] = useState(false);

  /* file-input ref (for uploads) */
  const fileRef = useRef<HTMLInputElement | null>(null);

  /* ---------- link-modal state ---------- */
  const [linkOpen, setLinkOpen] = useState(false);
  const [appsLoading, setAppsLoading] = useState(false);
  const [fdApps, setFdApps] = useState<IFdApplicationType[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<ID | null>(null);

  /* ---------------- catalogues ---------------- */
  useEffect(() => {
    setFlags((f) => ({...f, products: true}));
    apiGetProgramProducts()
      .then((p) => {
        setProducts(p);
      })
      .catch((err) => console.error('[Products] error:', err))
      .finally(() => setFlags((f) => ({...f, products: false})));
  }, []);

  useEffect(() => {
    setFlags((f) => ({...f, produce: true}));
    apiGetProduce()
      .then(setAllProduce)
      .catch((err) => console.error('[Produce] error:', err))
      .finally(() => setFlags((f) => ({...f, produce: false})));
  }, []);

  /* ---------------- handle side panel edits ---------------- */
  useEffect(() => {
    if (editState === 'saved') {
      // Program was updated in side panel, refresh the program data
      // For now, just reset the edit state since programs are managed in Redux
      // If program data was fetched from API, we would refetch it here
      reduxSetSidePanelEditState('idle');
    }
  }, [editState]);

  /* ---------------- farmer init ---------------- */
  const fetchFarmerRemote = useCallback(
    (email?: string, id?: number | string) => {
      setFlags((f) => ({...f, farmer: true}));
      const req = email ? apiGetFarmer(email) : apiGetFarmer(id as any);
      req
        .then(setFarmer)
        .catch((err) => console.error('[FarmerFetch] error:', err))
        .finally(() => setFlags((f) => ({...f, farmer: false})));
    },
    [],
  );

  useEffect(() => {
    if (program?.farmer) {
      setFarmer(program.farmer);
      return;
    }
    if (program?.farmerEmail || program?.farmerId) {
      fetchFarmerRemote(program.farmerEmail, program.farmerId);
    } else {
      setFarmer(undefined);
    }
  }, [program, fetchFarmerRemote]);

  /* ---------------- save ---------------- */
  const handleSave = () => {
    // program.id is already present when you opened /programs/:id
    apiGenerateKhulaProgram({...program, lines: programLines})
      .then(() => NavigationService.navigate('/programs'))
      .catch((err) => console.error('[Save] error:', err))
      .finally(() => setFlags((f) => ({...f, creating: false})));
  };

  /* ---------------- Excel template download ---------------- */
  const handleDownloadTemplate = async () => {
    try {
      let catalogue = products;
      if (catalogue.length === 0) {
        setFlags((f) => ({...f, products: true}));
        catalogue = await apiGetProgramProducts();
        setProducts(catalogue);
      }
      const blob = await buildProgramTemplate(catalogue);
      saveAs(blob, 'program-template.xlsx');
    } catch (err) {
      console.error('[Template] download error:', err);
      alert('Unable to build template. Please try again.');
    } finally {
      setFlags((f) => ({...f, products: false}));
    }
  };

  /* ---------------- Excel upload ---------------- */
  const handleExcelImport = async (file: File) => {
    try {
      if (!products.length) {
        alert('Product list still loading, please try again shortly.');
        return;
      }
      const buf = await file.arrayBuffer();
      const lines = await parseProgramTemplate(buf, products);
      lines.forEach(reduxAddCreateProgramLine);
      setSnack(true);
    } catch (err: any) {
      console.error('[Template] import error:', err);
      alert(err.message || 'Import failed – please check your file.');
    }
  };

  /* ---------------- AI Autocreate ---------------- */
  const handleAI = async () => {
    // Validate that we have a program and the key fields needed for AI
    if (!program) {
      alert(
        'Please save the program details (crop, hectares and type) before using AI Autocreate.',
      );
      return;
    }

    if (!program.produceId || !program.hectares || !program.type) {
      alert(
        'AI Autocreate needs a crop, hectares and program type. Please fill these in on the left and save first.',
      );
      return;
    }

    setFlags((f) => ({...f, ai: true}));
    try {
      const s: any = program.fieldPolygon?.soilData || {};
      const soil: ISoilData = {
        Ca: s.Ca ?? 0,
        K: s.K ?? 0,
        Mg: s.Mg ?? 0,
        Na: s.Na ?? 0,
        P: s.P ?? 0,
        ph: s.pH ?? 0,
        exchangeableAcidity: s.exchangeableAcidity ?? 0,
        totalCations: s.cations ?? 0,
      };
      const crop =
        allProduce.find((p) => p.id === program.produceId)?.name || 'Crop';
      const programType = program.type || 'Fertiliser';
      const soilReq: ISoilSampling = {
        farmSize: program.hectares || 1,
        landType: 'Dry land',
        location: 'Unknown',
        plantDate: new Date().toISOString(),
        produce: crop,
        programType,
        soilData: soil,
        soilType: 'Unknown',
        target: 10,
        wateringType: 'Unknown',
      };
      const catalogue =
        products.length > 0 ? products : await apiGetProgramProducts();
      // Backend aiProgramInput type does not yet support programType,
      // so strip it out before calling the API to avoid GraphQL errors.
      const {programType: pType, ...soilReqForApi} =
        (soilReq as unknown as {
          programType?: string;
          [key: string]: any;
        }) || {};
      console.log('Stripping unused programType for API:', pType);
      const aiLines = await apiGenerateAiProgram(
        soilReqForApi as unknown as ISoilSampling,
      );

      aiLines.forEach((l: any) => {
        const prod = catalogue.find((p) => p.id === l.product.id);
        if (!prod) return;
        const {value, unit, coverage} = parseApplicationRate(l.applicationRate);
        reduxAddCreateProgramLine({
          productId: String(prod.id),
          when:
            l.applicationStage[0].toUpperCase() + l.applicationStage.slice(1),
          quantity:
            Math.ceil(value / (prod.size || 1)) * (program.hectares || 1),
          applicationRate: value,
          applicationUnit: unit,
          applicationType: coverage.includes('ha')
            ? 'Per Hectare'
            : `Per ${coverage}`,
          applicationTypePerHectare: coverage.includes('ha') ? 1 : 0,
          comment: `${l.applicationStyle}\n\nNB: ${l.notes}`,
        });
      });
    } catch (err: any) {
      console.error('[AI] error:', err);
      alert(
        `AI Autocreate failed. ${
          err?.message ? String(err.message) : 'Please try again later.'
        }`,
      );
    } finally {
      setFlags((f) => ({...f, ai: false}));
    }
  };

  /* ---------------- link-modal helpers (unchanged) ---------------- */
  const openLinkModal = async () => {
    const farmerId = farmer?.userId ?? program?.farmerId;
    if (!farmerId) return alert('Farmer not found.');
    setLinkOpen(true);
    setAppsLoading(true);
    try {
      const apps = (await apiGetUserFdApplications(farmerUserId)) || [];
      setFdApps(apps);
      setSelectedAppId(null);
    } catch (err) {
      console.error('[LinkModal] fetch error:', err);
    } finally {
      setAppsLoading(false);
    }
  };

  const handleLinkSave = async () => {
    if (!selectedAppId) return;
    if (!program?.id) return alert('Please save the program first.');
    try {
      const res = await apiLinkProgramToFundingApplication(
        program.id,
        selectedAppId,
      );
      alert(res.message);
    } catch (err) {
      console.error('[LinkModal] link error:', err);
    }
    setLinkOpen(false);
  };

  /* ---------------- display helpers ---------------- */
  const cropName =
    allProduce.find((p) => p.id === program?.produceId)?.name || '';
  const programType = program?.type || 'Fertiliser';

  /* ---------------------------------------------------------------------- */
  /*  render                                                                */
  /* ---------------------------------------------------------------------- */
  return (
    <Box className="create-program">
      {/* hidden file input for Excel uploads */}
      <input
        type="file"
        accept=".xlsx"
        ref={fileRef}
        style={{display: 'none'}}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleExcelImport(f);
          e.target.value = '';
        }}
      />

      {/* back nav */}
      <Box
        className="back-row"
        onClick={() => NavigationService.navigate('/programs')}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <Typography variant="body1" className="back-text">
          Back to Programs
        </Typography>
      </Box>

      {/* content */}
      <Box className="content-wrapper">
        <ProgramCard
          farmer={farmer}
          program={program}
          cropName={cropName}
          programType={programType}
          onEdit={() =>
            reduxToggleSidePanel(
              true,
              {panelType: ESidePanelView.ADD_PROGRAM, data: program},
              false,
            )
          }
          onSave={handleSave}
        />

        {/* line-action buttons */}
        <Box className="line-actions-row">
          <Button
            className="add-line-btn"
            onClick={() =>
              reduxToggleSidePanel(
                true,
                {panelType: ESidePanelView.ADD_PROGRAM_LINE},
                false,
              )
            }
          >
            + Add New Product
          </Button>

          <Button className="auto-create-btn" onClick={handleAI}>
            <FontAwesomeIcon icon={faSparkles} className="ai-icon" />
            AI Autocreate
          </Button>

          <Button className="add-line-btn" onClick={openLinkModal}>
            Link to Funding Application
          </Button>

          <Button
            className="add-line-btn"
            onClick={handleDownloadTemplate}
            disabled={flags.products}
          >
            Download Excel Template
          </Button>

          <Button variant="outlined" onClick={() => fileRef.current?.click()}>
            Upload Excel
          </Button>
        </Box>

        {/* AI loader or table */}
        {flags.ai ? (
          <Box className="ai-loader-container">
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {preserveAspectRatio: 'xMidYMid slice'},
              }}
              height={300}
              width={300}
            />
            <Typography className="ai-loader-message">
              Generating suggestions…
            </Typography>
          </Box>
        ) : (
          <ProgramLineTable
            lines={programLines || []}
            allProducts={products}
            onEdit={(i) =>
              reduxToggleSidePanel(
                true,
                {
                  panelType: ESidePanelView.EDIT_PROGRAM_LINE,
                  data: {index: i, data: programLines?.[i]},
                },
                false,
              )
            }
            onCopy={(i) => console.log('[Table] copy line', i)}
            onRemove={(i) => reduxRemoveCreateProgramLine(i)}
            onMoveUp={(i) => reduxMoveCreateProgramLineUp(i)}
            onMoveDown={(i) => reduxMoveCreateProgramLineDown(i)}
          />
        )}

        {/* snackbar for generic messages */}
        <Snackbar
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          open={snack}
          onClose={() => setSnack(false)}
          autoHideDuration={3000}
          message="Lines imported from Excel"
        />
      </Box>

      {/* link-modal */}
      <Dialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Funding Application</DialogTitle>
        <DialogContent dividers>
          {appsLoading ? (
            <CircularProgress size={32} />
          ) : fdApps.length === 0 ? (
            <Typography>
              No funding applications found for this farmer.
            </Typography>
          ) : (
            <List>
              {fdApps.map((app) => (
                <ListItem
                  button
                  key={app.id}
                  selected={app.id === selectedAppId}
                  onClick={() => setSelectedAppId(app.id)}
                >
                  <Radio checked={app.id === selectedAppId} />
                  <ListItemText
                    primary={`Application #${app.id}`}
                    secondary={`Created: ${app.createdAt?.substring(
                      0,
                      10,
                    )} • State: ${app.applicationState} • Ask: R ${
                      app.fundingAsk?.fundingRequestCosts
                        ?.reduce((acc, c) => acc + (c.cost || 0), 0)
                        .toLocaleString('en-ZA') ?? '_'
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLinkSave}
            disabled={!selectedAppId}
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ====================================================================== */
/*  Summary card                                                           */
/* ====================================================================== */
function ProgramCard(props: {
  farmer?: IFarmer;
  program?: IFarmInputProgramInput;
  cropName: string;
  programType: string;
  onEdit: VoidFunction;
  onSave: VoidFunction;
}) {
  const {farmer, program, cropName, programType, onEdit, onSave} = props;

  const farmName =
    farmer?.farm?.farmName ??
    farmer?.farm?.name ??
    (program as any)?.farmName ??
    '';
  const farmerName =
    farmer && (farmer.name || farmer.surname)
      ? `${farmer.name} ${farmer.surname}`.trim()
      : (program as any)?.farmer?.name
      ? `${(program as any).farmer.name} ${
          (program as any).farmer.surname
        }`.trim()
      : '';
  const farmerDisplay = farmerName || farmName || 'Unknown farmer';

  const headerTitle =
    cropName || programType
      ? `${cropName ? cropName + ' ' : ''}${programType} Program`
      : 'Untitled Program';

  // Get field display name
  const fieldDisplay = (program as any)?.fieldPolygon?.info?.fieldName
    ? `${(program as any).fieldPolygon.info.fieldName}${
        (program as any).fieldPolygon.info.hectares
          ? ` - ${(program as any).fieldPolygon.info.hectares}ha`
          : ''
      }`
    : program?.fieldId
    ? `Field ${program.fieldId}`
    : 'Field 1';

  return (
    <Card className="program-card">
      <Box className="status-tag">Draft</Box>
      <Box className="top-row">
        <Typography className="program-name">{headerTitle}</Typography>
        <Box className="program-actions">
          <Button className="edit-program-btn" onClick={onEdit}>
            <FontAwesomeIcon icon={faPenToSquare} className="edit-icon" />
            Edit Program
          </Button>
          <Button className="save-program-btn" onClick={onSave}>
            <FontAwesomeIcon icon={faSave} className="icon-left" />
            Save Program
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Tile title="Farmer" value={farmerDisplay} />
        <Tile title="Field" value={fieldDisplay} />
        <Tile title="Crop" value={cropName || '_'} />
        <Tile title="Hectares" value={program?.hectares || '_'} />
        <Tile title="Program type" value={programType} />
        <Tile
          title="Soil details"
          value={
            <Button
              className="view-results-btn"
              onClick={() => {
                const soil = (program as any)?.fieldPolygon?.soilData;
                if (!soil || Object.keys(soil).length === 0) {
                  return alert(
                    'No soil data found. Please add soil data in the edit form.',
                  );
                }
                reduxToggleSidePanel(
                  true,
                  {
                    panelType: ESidePanelView.ADD_SOIL_DATA,
                    data: soil,
                    readOnly: true,
                  },
                  false,
                );
              }}
            >
              {(program as any)?.fieldPolygon?.soilData &&
              Object.keys((program as any).fieldPolygon.soilData).length > 0
                ? 'View results'
                : 'Add soil data'}
              <FontAwesomeIcon icon={faArrowRight} className="icon-left" />
            </Button>
          }
        />
      </Grid>
    </Card>
  );
}

function Tile({title, value}: {title: string; value?: React.ReactNode}) {
  return (
    <Grid item xs={12} sm={6} md={4} className="program-card-tile">
      <Typography className="tile-title">{title}</Typography>
      <Typography className="tile-value" variant="body1">
        {value}
      </Typography>
    </Grid>
  );
}

export default ProgramCreate;
